import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
// Import the api utility
import api from "../../utils/api";
import {
  loadingAlert,
  closeAlert,
  successAlert,
  errorAlert,
  confirmAlert,
} from "../../utils/swalConfig";

const CryptoManagement = () => {
  const [networks, setNetworks] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    symbol: "",
    walletAddress: "",
    exchangeRate: "",
    minimumDeposit: "",
    minimumWithdrawal: "",
    isActive: true,
  });

  const fetchNetworks = async () => {
    try {
      loadingAlert("Loading crypto networks...");
      // Replace direct fetch with api utility
      const response = await api.get("/crypto/networks");
      if (response.data.status === "success") {
        setNetworks(response.data.data.networks);
      }
      closeAlert();
    } catch (error) {
      closeAlert();
      errorAlert("Error!", "Failed to fetch networks");
    }
  };

  useEffect(() => {
    fetchNetworks();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      loadingAlert(isEdit ? "Updating network..." : "Creating network...");
      const url = isEdit ? `/crypto/${selectedNetwork._id}` : "/crypto";

      // Replace direct fetch with api utility
      const response = isEdit
        ? await api.patch(url, formData)
        : await api.post(url, formData);

      if (response.data.status === "success") {
        closeAlert();
        successAlert(
          "Success!",
          `Network ${isEdit ? "updated" : "created"} successfully`
        );
        fetchNetworks();
        handleClose();
      }
    } catch (error) {
      closeAlert();
      errorAlert("Error!", "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmAlert(
      "Delete Network",
      "Are you sure you want to delete this network?",
      "warning"
    );

    if (result.isConfirmed) {
      try {
        loadingAlert("Deleting network...");
        // Replace direct fetch with api utility
        const response = await api.delete(`/crypto/${id}`);

        if (response.status === 204 || response.status === 200) {
          closeAlert();
          successAlert("Success!", "Network deleted successfully");
          fetchNetworks();
        }
      } catch (error) {
        closeAlert();
        errorAlert("Error!", "Failed to delete network");
      }
    }
  };

  const handleEdit = (network) => {
    setSelectedNetwork(network);
    setFormData({
      name: network.name,
      symbol: network.symbol,
      walletAddress: network.walletAddress,
      exchangeRate: network.exchangeRate,
      minimumDeposit: network.minimumDeposit,
      minimumWithdrawal: network.minimumWithdrawal,
      isActive: network.isActive,
    });
    setIsEdit(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    setSelectedNetwork(null);
    setFormData({
      name: "",
      symbol: "",
      walletAddress: "",
      exchangeRate: "",
      minimumDeposit: "",
      minimumWithdrawal: "",
      isActive: true,
    });
  };

  return (
    <Box p={3}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Typography variant="h5">Crypto Networks Management</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Network
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Wallet Address</TableCell>
                  <TableCell>Exchange Rate</TableCell>
                  <TableCell>Min. Deposit</TableCell>
                  <TableCell>Min. Withdrawal</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {networks.map((network) => (
                  <TableRow key={network._id}>
                    <TableCell>{network.name}</TableCell>
                    <TableCell>{network.symbol}</TableCell>
                    <TableCell>{network.walletAddress}</TableCell>
                    <TableCell>
                      ₦{network.exchangeRate.toLocaleString()}
                    </TableCell>
                    <TableCell>${network.minimumDeposit}</TableCell>
                    <TableCell>${network.minimumWithdrawal}</TableCell>
                    <TableCell>
                      {network.isActive ? "Active" : "Inactive"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEdit(network)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(network._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {isEdit ? "Edit Network" : "Add New Network"}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Network Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Symbol"
                  value={formData.symbol}
                  onChange={(e) =>
                    setFormData({ ...formData, symbol: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wallet Address"
                  value={formData.walletAddress}
                  onChange={(e) =>
                    setFormData({ ...formData, walletAddress: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Exchange Rate (₦)"
                  value={formData.exchangeRate}
                  onChange={(e) =>
                    setFormData({ ...formData, exchangeRate: e.target.value })
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Deposit ($)"
                  value={formData.minimumDeposit}
                  onChange={(e) =>
                    setFormData({ ...formData, minimumDeposit: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Minimum Withdrawal ($)"
                  value={formData.minimumWithdrawal}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      minimumWithdrawal: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained">
              {isEdit ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CryptoManagement;
